import { Injectable, signal } from '@angular/core';

/**
 * Tracks chart/table cards waiting for runtime data after a user-initiated refresh
 * (date range, page filters). Drives `isPreviewDataLoading` on report canvas cards.
 */
@Injectable({ providedIn: 'root' })
export class CardRuntimeRefreshStateService {
	private readonly _loadingCardIds = signal<ReadonlySet<string>>(new Set());

	readonly loadingCardIds = this._loadingCardIds.asReadonly();

	isCardLoading(cardId: string): boolean {
		return this._loadingCardIds().has(cardId);
	}

	beginCardRefresh(cardId: string): void {
		this.#setLoading(cardId, true);
	}

	beginCardsRefresh(cardIds: readonly string[]): void {
		if (cardIds.length === 0) {
			return;
		}
		const next = new Set(this._loadingCardIds());
		for (const cardId of cardIds) {
			next.add(cardId);
		}
		this._loadingCardIds.set(next);
	}

	endCardRefresh(cardId: string): void {
		this.#setLoading(cardId, false);
	}

	endCardsRefresh(cardIds: readonly string[]): void {
		if (cardIds.length === 0) {
			return;
		}
		const next = new Set(this._loadingCardIds());
		for (const cardId of cardIds) {
			next.delete(cardId);
		}
		this._loadingCardIds.set(next);
	}

	clearAll(): void {
		if (this._loadingCardIds().size === 0) {
			return;
		}
		this._loadingCardIds.set(new Set());
	}

	#setLoading(cardId: string, loading: boolean): void {
		const current = this._loadingCardIds();
		const has = current.has(cardId);
		if (loading === has) {
			return;
		}
		const next = new Set(current);
		if (loading) {
			next.add(cardId);
		} else {
			next.delete(cardId);
		}
		this._loadingCardIds.set(next);
	}
}
